import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { Steps } from 'models/workflow-step';
import { useFormik } from 'formik';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import ExternalUsersDropdown from 'components/shared/external-users-dropdown/external-users-dropdown.component';
import { User } from 'models/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';

type AddUpdateStepDialogComponentProps = {
    open: boolean;
    step?: Steps;
    onDialogClose: () => void;
    onAdd: (step: Steps) => void;
    onUpdate: (step: Steps) => void;
    editInfoText: string;
};

const AddUpdateStepDialog: FC<AddUpdateStepDialogComponentProps> = ({
    open,
    onDialogClose,
    onAdd,
    onUpdate,
    step,
    editInfoText,
}: AddUpdateStepDialogComponentProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow']);
    const currentOrg = currentOrgVar();
    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );
    const isLabMillingEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_MILLING,
    );

    const validate = (values: Steps) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('workflow:VALIDATION:NAME');
        }
        if (values.maxHourInStep == null) {
            errors.maxHourInStep = translate('workflow:VALIDATION:MAX_HOUR');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            name: step?.name || '',
            description: step?.description || '',
            maxHourInStep: step?.maxHourInStep || 0,
            defaultExtUsers: step?.defaultExtUsers || [],
            useMillingDeadline: step?.useMillingDeadline || false,
            millingDeadlineDays: step?.millingDeadlineDays || null,
            initiatesSubcontract: step?.initiatesSubcontract || false,
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: (values: Steps, { resetForm }) => {
            if (step?._id) {
                onUpdate({ ...values, _id: step?._id });
            } else {
                onAdd({ ...values });
            }
            resetForm();
        },
    });

    const handleMaxHourChange = (maxHourInStep: string) => {
        formik.setFieldValue('maxHourInStep', parseInt(maxHourInStep));
    };

    const handleDialogClose = () => {
        onDialogClose();
        formik.resetForm();
    };

    const onAddExtUser = (user: User) => {
        const defaultExtUsers = [...formik.values.defaultExtUsers];
        defaultExtUsers.push(user);
        formik.setValues({ ...formik.values, defaultExtUsers });
    };

    const onRemoveExtUser = (user: User) => {
        const defaultExtUsers = [...formik.values.defaultExtUsers];
        const index = defaultExtUsers.findIndex((extUser) => (extUser as User)._id === user._id);
        if (index > -1) {
            defaultExtUsers.splice(index, 1);
            formik.setValues({ ...formik.values, defaultExtUsers });
        }
    };

    const handleMillingDeadlineChange = (event: ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('useMillingDeadline', event.target.checked);
        if (!event.target.checked) {
            formik.setFieldValue('millingDeadlineDays', null);
        }
    };

    const handleDeadlineDaysChange = (millingDeadlineDays: string) => {
        if (millingDeadlineDays) {
            formik.setFieldValue('millingDeadlineDays', parseInt(millingDeadlineDays));
        }
    };

    return (
        <DHDialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={handleDialogClose}
            title={step?._id ? translate('EDIT_STEP') : translate('ADD_STEP')}
            actions={
                <Button disabled={!formik.isValid} variant="contained" onClick={() => formik.handleSubmit()}>
                    {step?._id ? translate('common:UPDATE') : translate('common:ADD')}
                </Button>
            }
        >
            {step?._id ? (
                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                    {editInfoText}
                </Alert>
            ) : undefined}
            <Box mt={1}>
                <Grid container spacing={2}>
                    <Grid item xs={9}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                id="name"
                                label={translate('common:NAME')}
                                required
                                helperText={formik.touched.name && formik.errors.name}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                {...formik.getFieldProps('name')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <NumberOnlyTextField
                            inputProps={{
                                style: { textAlign: 'right' },
                            }}
                            label={translate('workflow:MAX_HOUR_IN_STEP')}
                            fullWidth
                            required
                            onValueChange={handleMaxHourChange}
                            onBlur={formik.handleBlur}
                            name={formik.getFieldProps('maxHourInStep').name}
                            value={formik.values.maxHourInStep}
                            helperText={formik.touched.maxHourInStep && formik.errors.maxHourInStep}
                            error={formik.touched.maxHourInStep && Boolean(formik.errors.maxHourInStep)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                id="description"
                                label={translate('common:DESCRIPTION')}
                                {...formik.getFieldProps('description')}
                            />
                        </FormControl>
                    </Grid>
                    {isExternalLabUsersEnabled ? (
                        <Grid item xs={12}>
                            <ExternalUsersDropdown
                                onAddExtUser={onAddExtUser}
                                onRemoveExtUser={onRemoveExtUser}
                                selectedUsers={formik.values.defaultExtUsers as User[]}
                            />
                        </Grid>
                    ) : undefined}
                    {/* DD-3488: the step opens the subcontract flow; the partner is chosen there. */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="initiatesSubcontract"
                                    checked={Boolean(formik.values.initiatesSubcontract)}
                                    onChange={(event) =>
                                        formik.setFieldValue('initiatesSubcontract', event.target.checked)
                                    }
                                    data-test="add-update-step.initiates-subcontract-checkbox"
                                />
                            }
                            label={translate('workflow:INITIATES_SUBCONTRACT')}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {translate('workflow:INITIATES_SUBCONTRACT_HELP')}
                        </Typography>
                    </Grid>
                    {isLabMillingEnabled ? (
                        <Grid item>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="useMillingDeadline"
                                            checked={formik.values.useMillingDeadline}
                                            onChange={handleMillingDeadlineChange}
                                        />
                                    }
                                    label={translate('workflow:USE_MILLING_DEADLINE')}
                                />
                            </Grid>
                            {formik.values.useMillingDeadline ? (
                                <Grid item xs={12}>
                                    <NumberOnlyTextField
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('workflow:MILLING_DEADLINE_DAYS')}
                                        fullWidth
                                        required
                                        onValueChange={handleDeadlineDaysChange}
                                        onBlur={formik.handleBlur}
                                        name={formik.getFieldProps('millingDeadlineDays').name}
                                        value={formik.values.millingDeadlineDays}
                                        helperText={
                                            formik.touched.millingDeadlineDays && formik.errors.millingDeadlineDays
                                        }
                                        error={
                                            formik.touched.millingDeadlineDays &&
                                            Boolean(formik.errors.millingDeadlineDays)
                                        }
                                    />
                                </Grid>
                            ) : undefined}
                        </Grid>
                    ) : undefined}
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default AddUpdateStepDialog;
